import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { getConversations, getConversationsMessages, postConversationsRead } from '@/api/common';
import { getTeachers, sendConversationMessage, startConversation } from '@/api/parent';
import { Banner, Empty, ErrorBox, Loading, Modal, PageTitle, errMsg, field, fmtDateTime, paged, unwrap } from '../ui';

interface Conv { id: number; type: string; subject: string | null; with: { id: number; name: string; role: string }[]; last_message: { body: string; at: string } | null; unread: number }
interface Msg { id: number; sender: string | null; mine: boolean; body: string; at: string }
interface TeacherGroup { student: string; teachers: { user_id: number; name: string; form_teacher: boolean; subjects: string[] }[] }

const title = (c: Conv) => c.subject || c.with.map((u) => u.name).join(', ') || 'Conversation';

function NewMessage({ initialTo, onClose, onSent }: { initialTo?: number; onClose: () => void; onSent: (id: number) => void }) {
  const teachers = useQuery({ queryKey: ['parent-teachers'], queryFn: () => unwrap<TeacherGroup[]>(getTeachers()) });
  const [to, setTo] = useState<number[]>(initialTo ? [initialTo] : []);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const key = useRef(crypto.randomUUID());
  const send = useMutation({
    mutationFn: () => startConversation({ user_ids: to, subject: subject || undefined, body }, key.current),
    onSuccess: (c: { id: number }) => onSent(c.id),
  });
  const toggle = (id: number) => setTo((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <Modal title="New message" onClose={onClose}>
      <div className="space-y-3">
        {teachers.isLoading ? <Loading /> : teachers.isError ? <ErrorBox error={teachers.error} /> : !teachers.data?.some((g) => g.teachers.length) ? <Empty text="No teachers available to message." /> : (
          <div className="max-h-48 overflow-auto border rounded-xl p-2 text-sm">
            {teachers.data.map((g) => (
              <div key={g.student}>
                <div className="text-xs text-slate-500 mt-1">{g.student}</div>
                {g.teachers.map((t) => <label key={t.user_id} className="flex flex-wrap gap-x-2 py-1"><input type="checkbox" checked={to.includes(t.user_id)} onChange={() => toggle(t.user_id)} />{t.name}{t.form_teacher && ' (form teacher)'}<span className="text-slate-500">{t.subjects.join(', ')}</span></label>)}
              </div>
            ))}
          </div>
        )}
        <input className={field} placeholder="Subject (optional)" maxLength={120} value={subject} onChange={(e) => setSubject(e.target.value)} />
        <textarea className={field} rows={4} placeholder="Message" maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)} />
        {send.isError && <p className="text-sm text-red-600">{errMsg(send.error)}</p>}
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={!to.length || !body.trim() || send.isPending} onClick={() => send.mutate()}>{send.isPending ? 'Sending…' : 'Send'}</Button></div>
      </div>
    </Modal>
  );
}

function Thread({ id }: { id: number }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const key = useRef(crypto.randomUUID());
  const end = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ['parent-messages', id], queryFn: () => unwrap<{ messages: Msg[] }>(getConversationsMessages(id)), refetchInterval: 10_000 });
  const msgs = [...(q.data?.messages ?? [])].sort((a, b) => a.id - b.id);

  useEffect(() => {
    postConversationsRead(id).then(() => qc.invalidateQueries({ queryKey: ['parent-conversations'] })).catch(() => undefined);
  }, [id, msgs.length, qc]);
  useEffect(() => { end.current?.scrollIntoView?.({ block: 'end' }); }, [msgs.length]);

  const send = useMutation({
    mutationFn: () => sendConversationMessage(id, text.trim(), key.current),
    onSuccess: () => { setText(''); setErr(''); key.current = crypto.randomUUID(); qc.invalidateQueries({ queryKey: ['parent-messages', id] }); qc.invalidateQueries({ queryKey: ['parent-conversations'] }); },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <div className="flex flex-col h-[60vh] sm:h-[28rem]">
      <div className="flex-1 overflow-auto space-y-2 pr-1">
        {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : !msgs.length ? <Empty text="No messages yet." /> : msgs.map((m) => (
          <div key={m.id} className={`max-w-[85%] sm:max-w-[75%] rounded-xl px-3 py-2 text-sm ${m.mine ? 'ml-auto bg-brand-600 text-white' : 'bg-slate-100'}`}>
            {!m.mine && <div className="text-xs font-semibold">{m.sender}</div>}
            <div className="whitespace-pre-wrap">{m.body}</div>
            <div className={`text-[10px] mt-1 ${m.mine ? 'text-white/70' : 'text-slate-400'}`}>{fmtDateTime(m.at)}</div>
          </div>
        ))}
        <div ref={end} />
      </div>
      {err && <p className="text-sm text-red-600 mt-2">{err}</p>}
      <form className="flex gap-2 mt-3" onSubmit={(e) => { e.preventDefault(); if (text.trim() && !send.isPending) send.mutate(); }}>
        <input className={field} placeholder="Type a message" maxLength={4000} value={text} onChange={(e) => setText(e.target.value)} />
        <Button type="submit" disabled={!text.trim() || send.isPending}>Send</Button>
      </form>
    </div>
  );
}

export function MessagesPage() {
  const qc = useQueryClient();
  const [active, setActive] = useState<number | null>(null);
  const [params, setParams] = useSearchParams();
  const preTo = Number(params.get('to')) || undefined;
  const [composing, setComposing] = useState(!!preTo);
  const [notice, setNotice] = useState('');
  const q = useQuery({ queryKey: ['parent-conversations'], queryFn: () => paged<Conv>(getConversations()), refetchInterval: 30_000 });
  const current = q.data?.data.find((c) => c.id === active);

  return (
    <div className="space-y-5">
      <PageTitle kicker="School" title="Messages" sub="Talk privately with your child's teachers." action={<Button onClick={() => setComposing(true)}>New message</Button>} />
      {notice && <Banner onClose={() => setNotice('')}>{notice}</Banner>}
      <div className="grid md:grid-cols-[300px_1fr] gap-4">
        <div className={active ? 'hidden md:block' : ''}><Card title="Conversations">
          {q.isLoading ? <Loading /> : q.isError ? <ErrorBox error={q.error} onRetry={() => q.refetch()} /> : !q.data?.data.length ? <Empty text="No conversations yet." /> : (
            <ul className="divide-y -mx-2">
              {q.data.data.map((c) => (
                <li key={c.id}><button className={`w-full text-left px-2 py-2 rounded-xl ${c.id === active ? 'bg-slate-100' : ''}`} onClick={() => setActive(c.id)}>
                  <div className="flex justify-between text-sm"><span className={c.unread ? 'font-semibold' : ''}>{title(c)}</span>{c.unread > 0 && <span className="text-xs bg-brand-600 text-white rounded-full px-2">{c.unread}</span>}</div>
                  <div className="text-xs text-slate-500 truncate">{c.last_message?.body}</div>
                </button></li>
              ))}
            </ul>
          )}
        </Card></div>
        <div className={active ? '' : 'hidden md:block'}>
          <Card title={current ? title(current) : 'Select a conversation'} action={active ? <button className="md:hidden text-sm font-semibold text-slate-600" onClick={() => setActive(null)}>← All</button> : undefined}>{active ? <Thread id={active} /> : <Empty text="Pick a conversation or start a new message to a teacher." />}</Card>
        </div>
      </div>
      {composing && <NewMessage initialTo={preTo} onClose={() => { setComposing(false); if (preTo) setParams({}); }} onSent={(id) => { setComposing(false); if (preTo) setParams({}); setActive(id); setNotice('Message sent.'); qc.invalidateQueries({ queryKey: ['parent-conversations'] }); }} />}
    </div>
  );
}
